/**
 * Embed session — token plumbing for the editor-in-an-iframe flow.
 *
 * A host product (e.g. Apptile) that owns a Tile app via an API client
 * embeds the editor as:
 *
 *   <iframe src="https://tile…/embed/app/<appId>#token=<embed JWT>">
 *
 * The tile_session cookie is SameSite=Lax and never flows in a cross-site
 * iframe, so the embed page authenticates every API call with the
 * short-lived, single-app token instead (x-user-token header — both
 * tile-backend middlewares accept it).
 *
 * initEmbedSession() must run BEFORE anything reads window.location or
 * issues a fetch (i.e. first thing in client/index.tsx). It:
 *   1. captures `#token=…` into module memory and strips it from the URL
 *      via history.replaceState — the token never survives into
 *      copy-pasted URLs, and never reaches a server log (fragments are
 *      not sent on the wire);
 *   2. rewrites the URL to carry `?appId=<id>` — the editor and ~30 of
 *      its call sites read the app id from location.search, so the embed
 *      route satisfies them instead of refactoring each one;
 *   3. wraps window.fetch to add `x-user-token` to every same-origin
 *      request — one choke point covering apiFetch AND the raw fetch()
 *      call sites;
 *   4. listens for `{type:'tile:embed-token', token}` postMessages from
 *      the embedding page (origin-pinned to the iframe's referrer) so the
 *      host can refresh the token before expiry without reloading the
 *      iframe;
 *   5. keeps that token alive — see "Staying alive" below.
 *
 * Staying alive
 * -------------
 * The host re-mints on a timer, but a timer alone loses to a sleeping
 * laptop: setInterval doesn't fire while the machine is suspended, so a
 * tab left open over lunch wakes up holding an expired token and every
 * request 401s until the host's next tick — up to a full interval later,
 * with autosave failing silently in between.
 *
 * So the iframe drives it too:
 *   - on wake (visibilitychange/focus) it checks its own token's `exp` and
 *     asks the host for a new one if it's gone or nearly gone;
 *   - a 401 on a same-origin call triggers a refresh and ONE retry, so the
 *     call that raced the expiry still succeeds;
 *   - `tile:embed-session` window events report active/refreshing/expired
 *     so the UI can say something instead of failing quietly.
 *
 * Requests are addressed to the host because only it can mint (that needs
 * the API key, which deliberately never reaches the browser).
 */

import { ALL_CAPABILITIES, isKnownCapability, type Capability } from './capabilities';

const EMBED_PATH_RE = /^\/embed\/app\/([A-Za-z0-9_-]{6,128})$/;

/** Refresh when the token has less than this left — covers clock skew. */
const EXPIRY_MARGIN_MS = 5 * 60 * 1000;
/** How long to wait for the host to answer a refresh request. */
const REFRESH_TIMEOUT_MS = 8000;
/** Don't hammer the host if many calls 401 at once. */
const REFRESH_COOLDOWN_MS = 3000;

let embedAppId: string | null = null;
let embedToken: string | null = null;
/** Origin allowed to postMessage token refreshes (the embedding page). */
let hostOrigin: string | null = null;

/** Resolvers waiting on the host's next token. */
let pendingRefresh: ((token: string | null) => void)[] = [];
let refreshInFlight = false;
let lastRefreshAt = 0;

export type EmbedSessionState = 'active' | 'refreshing' | 'expired';
let sessionState: EmbedSessionState = 'active';

/**
 * Capabilities the host has locked for this merchant, straight off the
 * token's `locked` claim. Empty outside an embed session, so the standalone
 * editor is never gated.
 *
 * It lives in the token rather than a postMessage or a side fetch for three
 * reasons: it's signed (tamper-evident), it can't race first paint, and it
 * rides the refresh path for free — a plan change is just the host minting a
 * new token and posting it in, exactly like an expiry refresh.
 */
let lockedCapabilities: ReadonlySet<string> = new Set();

/** Fires when the locked set changes, so React can re-render the gates. */
export const EMBED_CAPABILITIES_EVENT = 'tile:embed-capabilities';

/**
 * Adopt the `locked` claim from a freshly-accepted token.
 *
 * A key we don't recognise is the deny-list's one sharp edge: it locks
 * nothing, so a rename or typo on the host silently makes a paid feature
 * free. We can't fail closed on it (that would lock the editor on any host
 * that runs ahead of this build), so we log it instead — loudly enough to
 * show up in a bug report.
 */
function applyLockedClaim(token: string | null): void {
  const raw = claimsOf(token)?.locked;
  const keys = Array.isArray(raw) ? raw.filter((k): k is string => typeof k === 'string') : [];

  const unknown = keys.filter((k) => !isKnownCapability(k));
  if (unknown.length) {
    console.error(
      `[embed] host locked unknown capabilities: ${unknown.join(', ')}. ` +
        `This build enforces: ${ALL_CAPABILITIES.join(', ')}. ` +
        'Those keys are being IGNORED — a renamed key silently unlocks the feature.'
    );
  }

  const next = new Set(keys.filter(isKnownCapability));
  const changed =
    next.size !== lockedCapabilities.size || [...next].some((k) => !lockedCapabilities.has(k));
  lockedCapabilities = next;
  if (changed && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EMBED_CAPABILITIES_EVENT));
  }
}

/** Is this capability locked by the host? Always false outside an embed. */
export function isCapabilityLocked(capability: Capability): boolean {
  return lockedCapabilities.has(capability);
}

/** The full locked set — for diagnostics; prefer isCapabilityLocked(). */
export function getLockedCapabilities(): readonly string[] {
  return [...lockedCapabilities];
}

/**
 * Ask the host to sell the upgrade that unlocks this capability.
 *
 * The upsell deliberately lives on the host: it needs plans, pricing and a
 * checkout flow that Tile has no business knowing about, and rendering it
 * there puts it above the iframe instead of trapped inside it. We say what
 * the user wanted; the host decides which plan clears it.
 *
 * Returns false when there's no host (standalone editor), so callers can
 * fall back to their normal behaviour.
 */
export function requestCapabilityUnlock(capability: Capability): boolean {
  return postToHost({ type: 'tile:embed-upgrade', capability });
}

export function getEmbedSessionState(): EmbedSessionState {
  return sessionState;
}

function setSessionState(next: EmbedSessionState): void {
  if (sessionState === next) return;
  sessionState = next;
  window.dispatchEvent(new CustomEvent('tile:embed-session', { detail: { state: next } }));
}

/**
 * Read the token's claims. This is NOT verification — the server re-checks
 * the signature on every call. We only need `exp` (when to ask for a new
 * one) and `locked` (what to grey out), neither of which is a security
 * decision on this side.
 */
function claimsOf(token: string | null): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const body = token.split('.')[1];
    if (!body) return null;
    return JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

function expiryOf(token: string | null): number | null {
  const exp = claimsOf(token)?.exp;
  return typeof exp === 'number' ? exp : null;
}

function isStale(): boolean {
  const exp = expiryOf(embedToken);
  // Unreadable exp → assume fine; the server is the authority and a 401
  // will trigger a refresh anyway.
  if (exp === null) return false;
  return Date.now() > exp - EXPIRY_MARGIN_MS;
}

/**
 * Ask the host to mint a fresh token. Resolves with it, or null if the host
 * doesn't answer in time (it may be gone, logged out, or offline).
 * Concurrent callers share one in-flight request.
 */
function requestTokenFromHost(): Promise<string | null> {
  if (refreshInFlight) {
    return new Promise((resolve) => pendingRefresh.push(resolve));
  }
  if (Date.now() - lastRefreshAt < REFRESH_COOLDOWN_MS) {
    return Promise.resolve(null);
  }
  if (!postToHost({ type: 'tile:embed-token-request', appId: embedAppId })) {
    return Promise.resolve(null);
  }

  refreshInFlight = true;
  lastRefreshAt = Date.now();
  setSessionState('refreshing');

  return new Promise((resolve) => {
    pendingRefresh.push(resolve);
    window.setTimeout(() => {
      if (!refreshInFlight) return;
      refreshInFlight = false;
      const waiters = pendingRefresh;
      pendingRefresh = [];
      // Still holding a usable token? Then this was a proactive refresh
      // that went unanswered — nothing is broken yet.
      setSessionState(isStale() ? 'expired' : 'active');
      waiters.forEach((waiter) => waiter(null));
    }, REFRESH_TIMEOUT_MS);
  });
}

function acceptToken(token: string): void {
  embedToken = token;
  // Entitlements ride the token, so a refresh is also how a plan change
  // lands — no reload, no separate message to keep in sync.
  applyLockedClaim(token);
  refreshInFlight = false;
  const waiters = pendingRefresh;
  pendingRefresh = [];
  setSessionState('active');
  waiters.forEach((waiter) => waiter(token));
}

export function isEmbedSession(): boolean {
  return embedAppId !== null;
}

/**
 * Hand an intent to the embedding page instead of acting on it ourselves.
 *
 * Navigation is the case that matters: chrome like the logo's "back to apps"
 * would replace the iframe's contents with Tile's own dashboard, stranding
 * the user inside the host's layout. The host owns its navigation, so we
 * tell it what was asked for and let it decide where to go.
 *
 * Returns false when there's no host to tell (normal, non-embedded page), so
 * callers can fall back to their default behaviour:
 *
 *   if (!postToHost({ type: 'tile:embed-navigate', target: 'home' })) {
 *     window.location.assign('/apps');
 *   }
 */
export function postToHost(message: Record<string, unknown>): boolean {
  if (!isEmbedSession() || typeof window === 'undefined') return false;
  if (!window.parent || window.parent === window) return false;
  // Target the referrer's origin when we know it. These messages carry
  // intents, never the token, so the '*' fallback leaks nothing.
  window.parent.postMessage(message, hostOrigin ?? '*');
  return true;
}

export function getEmbedToken(): string | null {
  return embedToken;
}

function isSameOriginUrl(input: RequestInfo | URL): boolean {
  const url =
    typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
  if (url.startsWith('//')) return false; // protocol-relative → cross-origin risk, skip
  if (url.startsWith('/')) return true;
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * A body we can send a second time. Streams and one-shot sources can't be
 * replayed, so a request carrying one gets the 401 back as-is (the refresh
 * still fires, so the NEXT call succeeds).
 */
function isReplayableBody(body: BodyInit | null | undefined): boolean {
  if (body == null || typeof body === 'string') return true;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return true;
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return true;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return true;
  if (body instanceof ArrayBuffer) return true;
  return false;
}

function installFetchTokenInjector(): void {
  const orig = window.fetch.bind(window);

  const withToken = (init: RequestInit | undefined, input: RequestInfo | URL, token: string) => {
    // Merge onto whatever headers the caller supplied. Headers may be a
    // Headers instance, an array, or a plain object — Headers normalizes
    // all three. A Request-object input carries its own headers; copy the
    // token onto the init so it wins regardless. `Request` is guarded
    // because it's absent in non-browser environments (jsdom, SSR) where
    // a bare `instanceof` would throw.
    const fromRequest =
      typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined;
    const headers = new Headers(init?.headers ?? fromRequest);
    headers.set('x-user-token', token);
    return { ...init, headers };
  };

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    if (!embedToken || !isSameOriginUrl(input)) return orig(input, init);

    const res = await orig(input, withToken(init, input, embedToken));
    if (res.status !== 401) return res;

    // 401 means the token lapsed mid-session (the server rejects expired
    // embed tokens outright). Get a fresh one and replay once — otherwise
    // this would surface as a silent autosave failure.
    const fresh = await requestTokenFromHost();
    if (!fresh || !isReplayableBody(init?.body)) return res;
    return orig(input, withToken(init, input, fresh));
  };
}

/**
 * Proactive refresh: a suspended machine's timers don't fire, so check the
 * token whenever the page comes back to life rather than trusting the
 * host's interval to have kept up.
 */
function installWakeRefresh(): void {
  const check = () => {
    if (!embedToken || document.hidden) return;
    if (isStale()) void requestTokenFromHost();
  };
  document.addEventListener('visibilitychange', check);
  window.addEventListener('focus', check);
  window.addEventListener('online', check);
}

function listenForTokenRefresh(): void {
  window.addEventListener('message', (event: MessageEvent) => {
    const data = event.data;
    if (!data || data.type !== 'tile:embed-token' || typeof data.token !== 'string') return;
    // Pin to the embedding page's origin when the referrer gave us one;
    // otherwise accept (worst case a hostile parent swaps in a token for
    // an app IT owns — it can never read ours back out).
    if (hostOrigin && event.origin !== hostOrigin) return;
    acceptToken(data.token);
  });
}

/**
 * No-op unless the current URL is /embed/app/:appId. Returns true when an
 * embed session was initialized.
 */
export function initEmbedSession(): boolean {
  if (typeof window === 'undefined') return false;
  const match = window.location.pathname.match(EMBED_PATH_RE);
  if (!match) return false;

  embedAppId = match[1];

  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  embedToken = hashParams.get('token');
  applyLockedClaim(embedToken);

  try {
    hostOrigin = document.referrer ? new URL(document.referrer).origin : null;
  } catch {
    hostOrigin = null;
  }

  // Strip the token from the URL, plant ?appId= for the editor.
  const search = new URLSearchParams(window.location.search);
  search.set('appId', embedAppId);
  window.history.replaceState(null, '', `${window.location.pathname}?${search.toString()}`);

  installFetchTokenInjector();
  listenForTokenRefresh();
  installWakeRefresh();
  return true;
}
